import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../common/general/CustomProgressBar/CustomProgressBar";
import {
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
  PlusCircleIcon,
  CheckSquareIcon,
  SquareIcon,
} from "@phosphor-icons/react";
import { ProjectRequirement, ProjectSubmission, ProjectSubmissionStatus } from "@axio/graphql/generated/graphql";
import ProjectSubmittedSection from "./ProjectSubmittedSection";
import ProjectGradedSection from "./ProjectGradedSection";

const ProjectRequirementsDashboard = ({
  submission,
  onMarkCompleted,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onAddProjectFile,
  loading = false,
}: {
  submission: ProjectSubmission;
  onMarkCompleted: (id: string, completed: boolean) => void;
  onAddTask: () => void;
  onEditTask: (requirement: ProjectRequirement) => void;
  onDeleteTask: (id: string) => void;
  onAddProjectFile: () => void;
  loading: boolean;
}) => {
  const [filter, setFilter] = useState("all");

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#F04438";
      case "medium":
        return "#F79009";
      case "low":
        return "#12B76A";
      default:
        return "#667085";
    }
  };

  // Determine project status based on submission and project data
  const getProjectStatus = (): ProjectSubmissionStatus => {
    return submission?.status;
  };

  const filteredRequirements =
    submission?.requirements?.filter((req) => {
      if (filter === "all") return true;
      if (filter === "completed") return req.completed;
      if (filter === "pending") return !req.completed;
      return req.type === filter;
    }) || [];

  const completedCount = submission?.requirements?.filter((req) => req.completed).length || 0;
  const totalCount = submission?.requirements?.length || 0;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const filterOptions = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "completed", label: "Completed" },
  ];

  const projectStatus = getProjectStatus();

  const projectCompleted = submission?.status === ProjectSubmissionStatus.Completed && submission?.isCompleted;
  const isGraded = projectCompleted && submission?.hasTeacherGraded;

  return (
    <View style={styles.container}>
      {projectCompleted && (
        <>
          {isGraded ? (
            <ProjectGradedSection submission={submission} />
          ) : (
            <ProjectSubmittedSection submission={submission} />
          )}
        </>
      )}

      {!projectCompleted && (
        <View style={styles.progressSection}>
          <View style={styles.progressInfo}>
            <CustomText
              style={styles.progressTitle}
              text="Overall Progress"
              useTranslationText={false}
              aria-level="2"
              role="heading"
            />
            <CustomText
              style={styles.progressText}
              text={`${completedCount} of ${totalCount} requirements completed`}
              useTranslationText={false}
            />
          </View>
          <View style={styles.progressBarContainer}>
            <CustomProgressBar
              progress={progressPercentage}
              color="#12B76A"
              unfilledColor="#EAECF0"
              ariaLabel="Project Progress"
            />
          </View>
          <CustomText style={styles.progressPercentage} text={`${progressPercentage}%`} useTranslationText={false} />
        </View>
      )}

      <View style={styles.filtersSection}>
        <View style={styles.filtersRow}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersContainer} tabIndex={0}>
            {filterOptions.map((option) => (
              <TouchableOpacity
                key={option.key}
                style={[styles.filterButton, filter === option.key && styles.activeFilterButton]}
                onPress={() => setFilter(option.key)}
                role="button"
                accessibilityLabel={`Filter ${option.label}`}
                accessibilityHint={`Apply the ${option.label} filter`}>
                <CustomText
                  style={[styles.filterText, filter === option.key && styles.activeFilterText]}
                  text={option.label}
                  useTranslationText={false}
                />
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Add Task Button - always show for testing */}
          <View style={styles.actionButtons}>
            {/* <CustomButton
              text="Add File"
              size="s"
              styleType="primary"
              onPress={() => onAddProjectFile && onAddProjectFile()}
              style={styles.addTaskButton}
              icon={<Plus size={16} color="#ffffff" />}
            /> */}
            <CustomButton
              text="Add Task"
              leftIcon={<PlusCircleIcon style={styles.addIcon} aria-hidden="true" />}
              size="s"
              styleType="primaryLight"
              onPress={() => onAddTask && onAddTask()}
              style={styles.addTaskButton}
              icon={<PlusIcon size={16} color="#ffffff" />}
            />
          </View>
        </View>
      </View>

      <ScrollView style={styles.requirementsList} showsVerticalScrollIndicator={false} tabIndex={0}>
        {filteredRequirements.length === 0 ? (
          <View style={styles.emptyState}>
            <CustomText
              style={styles.emptyText}
              text="No requirements found for the selected filter"
              useTranslationText={false}
            />
          </View>
        ) : (
          filteredRequirements
            .slice()
            .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())
            .map((requirement) => (
              <View key={requirement.id} style={styles.requirementCard}>
                <TouchableOpacity
                  role="button"
                  accessibilityLabel={`Mark ${requirement.title} as ${requirement.completed ? "incomplete" : "complete"}`}
                  accessibilityHint={`Mark ${requirement.title} as ${requirement.completed ? "incomplete" : "complete"}`}
                  style={styles.checkboxContainer}
                  onPress={() => onMarkCompleted(requirement.id, !requirement.completed)}
                  disabled={loading || projectStatus === "COMPLETED"}>
                  {requirement.completed ? (
                    <CheckSquareIcon size={24} color="#12B76A" weight="fill" />
                  ) : (
                    <SquareIcon size={24} color="#98A2B3" />
                  )}
                </TouchableOpacity>
                <View style={styles.requirementData}>
                  <View style={styles.requirementHeader}>
                    <View style={styles.requirementTitleRow}>
                      <View style={styles.requirementTitleContainer}>
                        <CustomText
                          style={[styles.requirementTitle, requirement.completed && styles.completedTitle]}
                          text={requirement.title}
                          useTranslationText={false}
                          aria-level="3"
                          role="heading"
                        />
                        <View style={styles.requirementMeta}>
                          <CustomText
                            text={requirement.type}
                            style={styles.requirementType}
                            useTranslationText={false}
                          />
                        </View>
                      </View>
                    </View>
                  </View>

                  {requirement.description && (
                    <View style={styles.requirementDescription}>
                      <CustomText
                        style={styles.descriptionText}
                        text={requirement.description}
                        useTranslationText={false}
                      />
                    </View>
                  )}

                  {requirement.dependencies && requirement.dependencies.length > 0 && (
                    <View style={styles.dependenciesSection}>
                      <CustomText style={styles.dependenciesLabel} text="Dependencies:" useTranslationText={false} />
                      {requirement.dependencies.map((dep, index) => (
                        <CustomText
                          key={index}
                          style={styles.dependencyText}
                          text={`• ${dep}`}
                          useTranslationText={false}
                        />
                      ))}
                    </View>
                  )}

                  {requirement.completed && requirement.completedDate && (
                    <View style={styles.completedInfo}>
                      <CheckCircleIcon size={16} color="#12B76A" />
                      <CustomText
                        style={styles.completedText}
                        text={`Completed on ${new Date(requirement.completedDate).toLocaleDateString()}`}
                        useTranslationText={false}
                      />
                    </View>
                  )}
                </View>

                {/* Task Actions - always show for testing */}
                <View style={styles.taskActions}>
                  {requirement.estimatedHours && (
                    <View style={styles.estimatedHoursWrapper}>
                      <ClockIcon size={18} color="#667085" />
                      <CustomText
                        style={styles.estimatedHours}
                        text={`${requirement.estimatedHours}h`}
                        useTranslationText={false}
                      />
                    </View>
                  )}
                  {requirement.dueDate && (
                    <>
                      <View style={styles.metaSeparator} />
                      <CalendarIcon size={14} color="#667085" />
                      <CustomText
                        style={styles.dueDate}
                        text={new Date(requirement.dueDate).toLocaleDateString()}
                        useTranslationText={false}
                      />
                    </>
                  )}
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => onEditTask && onEditTask(requirement)}
                    role="button"
                    accessibilityLabel={`Edit ${requirement.title} task`}
                    accessibilityHint={`Edit the task: ${requirement.title}`}>
                    <PencilSimpleIcon size={16} color="#667085" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => onDeleteTask && onDeleteTask(requirement.id)}
                    role="button"
                    accessibilityLabel={`Delete ${requirement.title} task`}
                    accessibilityHint={`Delete the task: ${requirement.title}`}>
                    <TrashIcon size={16} color="#F04438" />
                  </TouchableOpacity>
                </View>
              </View>
            ))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    marginBottom: 100,
  },
  completedText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#12B76A",
    marginLeft: 4,
  },
  progressSection: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  progressInfo: {
    flex: 1,
    marginRight: 16,
  },
  progressTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#101828",
    marginBottom: 4,
  },
  progressText: {
    fontSize: 14,
    color: "#667085",
  },
  progressBarContainer: {
    flex: 2,
    marginRight: 16,
  },
  progressPercentage: {
    fontSize: 16,
    fontWeight: "600",
    color: "#101828",
    minWidth: 40,
    textAlign: "right",
  },
  filtersSection: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  filtersRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  filtersContainer: {
    flex: 1,
    marginRight: 16,
  },
  addTaskButton: {
    minWidth: 100,
    fontWeight: 700,
  },
  addIcon: {
    color: "#3E68FE",
    fontWeight: 700,
    fontSize: 20,
  },
  filterButton: {
    padding: 10,
    marginRight: 8,
    borderRadius: 8,
    backgroundColor: "#FFF",
  },
  activeFilterButton: {
    backgroundColor: "#EAECF0",
  },
  filterText: {
    fontSize: 14,
    color: "#667085",
    fontWeight: "400",
  },
  activeFilterText: {
    fontWeight: 700,
    color: "#000",
  },
  requirementsList: {
    flex: 1,
    paddingVertical: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 16,
    color: "#667085",
    textAlign: "center",
  },
  requirementCard: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  requirementHeader: {},
  requirementTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  checkboxContainer: {
    marginRight: 12,
    marginTop: 2,
  },
  requirementTitleContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
  },
  requirementTitle: {
    fontWeight: 600,
    fontSize: 16,
    color: "#101828",
  },
  completedTitle: {
    textDecorationLine: "line-through",
    color: "#667085",
  },
  requirementMeta: {
    flexDirection: "row",
    alignItems: "center",
  },
  metaSeparator: {
    width: 1,
    height: 12,
    backgroundColor: "#EAECF0",
    marginHorizontal: 8,
  },
  estimatedHours: {
    lineHeight: 12,
    fontSize: 12,
    color: "#667085",
    marginLeft: 4,
  },
  dueDate: {
    fontSize: 12,
    color: "#667085",
    marginLeft: 4,
  },
  requirementDescription: {
    marginBottom: 12,
    fontWeight: 400,
  },
  descriptionText: {
    fontWeight: 400,
    fontSize: 14,
    color: "#667085",
    lineHeight: 20,
  },
  dependenciesSection: {
    paddingLeft: 48,
    marginBottom: 12,
  },
  dependenciesLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#101828",
    marginBottom: 4,
  },
  dependencyText: {
    fontSize: 12,
    color: "#667085",
    marginBottom: 2,
  },
  completedInfo: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 48,
    marginTop: 8,
  },
  taskActions: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 8,
  },
  actionButton: {
    padding: 8,
    borderRadius: 4,
    marginLeft: 4,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  actionButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  requirementData: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },
  estimatedHoursWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    paddingHorizontal: 12,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderRadius: 24,
    borderColor: "#EAECF0",
  },
  requirementType: {
    marginLeft: 8,
    fontSize: 12,
    lineHeight: 18,
    borderRadius: 24,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: "#913DAF",
    backgroundColor: "#913DAF1A",
  },
});

export default ProjectRequirementsDashboard;
